import useLocalStorage from "hooks/useLocalStorage"

export const DOWNLOAD_TYPES = Object.freeze({
  EXCEL: "excel",
  PDF: "pdf",
  BC3: "bc3",
})

export const INPUTS = Object.freeze({
  CLIENT_PRICE: "clientPrice",
  COST_PRICE: "costPrice",
  PART_DECOMPOSITION: "decomposition",
  MEASURES: "measures",
  COMMENTS: "comment",
  PDF_PRICE_OPTION: "pdfPriceValue",
  COMPANY_AND_CLIENT_INFO: "companyAndClientInfo",
  GENERAL_CONDITIONS: "generalConditions",
  BRIEFING_POSITION: "briefingPosition",
  ADD_COVER: "addCover",
  PDF_MEASUREMENTS: "pdfMeasurements",
  ADD_SIGNATURE: "addSignature",
  ADD_DATE: "addDate",
  BORDES: "bordes",
  NEW_FORMAT: "newFormat",
  SIMPLE_MODE: "simpleMode",
  EXPORT_MEASUREMENTS: "export_measurements",
  USE_COSTS: "use_costs",
  EXPORT_DECOMPOSITIONS: "export_decompositions",
  DECOMPOSITION_FILTER: "decompositionFilter",
  CERTIFICATION_AT_ORIGIN: "certificationAtOrigin",
  INCLUDE_BUDGET: "includeBudget",
  INCLUDE_UNCERTIFIED_LINE_ITEMS: "includeUncertifiedLineItems",
  CERTIFICATION_PDF_FORMAT: "certificationPdfFormat",
})

export const DOWNLOAD_BUDGET_CONFIG = "downloadBudgetConfig"

/**
 * Custom hook for managing the download configuration of budget data in PDF or Excel formats.
 *
 * This hook provides a way to persistently store and update download settings using `useLocalStorage`.
 * It organizes configurations into two sections: PDF and Excel, each with their own set of inputs and options.
 *
 * @returns {[Object, Function]} An array containing the current download configuration and an updater function.
 * @returns {Object} downloadBudgetConfig - The current configuration for PDF and Excel downloads.
 * @returns {Function} updateConfig - Updates a specific configuration value within a given section.
 *
 * @example
 * const [downloadBudgetConfig, updateConfig] = useDownloadBudgetConfig();
 *
 * // Access PDF configuration
 * const pdfConfig = downloadBudgetConfig[DOWNLOAD_TYPES.PDF];
 *
 * // Update the PDF price option
 * updateConfig(DOWNLOAD_TYPES.PDF, INPUTS.PDF_PRICE_OPTION, "cost");
 *
 * @description
 * - `downloadBudgetConfig`: Stores the current settings for PDF and Excel downloads.
 * - `updateConfig`: Updates a specific key-value pair in the configuration for a given section (PDF or Excel).
 *
 * ### Default Configuration
 * #### PDF
 * - `pdfPriceValue`: "client"
 * - `companyAndClientInfo`: `true`
 * - `generalConditions`: `true`
 * - `decomposition`: `true`
 * - `briefingPosition`: `false`
 * - `addCover`: `true`
 * - `addSignature`: `false`
 * - `addDate`: `false`

 *
 * #### Excel
 * - `clientPrice`: `true`
 * - `costPrice`: `false`
 * - `decomposition`: `true`
 * - `measures`: `false`
 * - `comment`: `false`
 */

const useDownloadBudgetConfig = () => {
  const [downloadBudgetConfig, setDownloadBudgetConfig] = useLocalStorage(
    DOWNLOAD_BUDGET_CONFIG,
    {
      [DOWNLOAD_TYPES.PDF]: {
        [INPUTS.PDF_PRICE_OPTION]: "client",
        [INPUTS.COMPANY_AND_CLIENT_INFO]: true,
        [INPUTS.GENERAL_CONDITIONS]: true,
        [INPUTS.PART_DECOMPOSITION]: true,
        [INPUTS.BRIEFING_POSITION]: false,
        [INPUTS.PDF_MEASUREMENTS]: true,
        [INPUTS.ADD_COVER]: true,
        [INPUTS.ADD_SIGNATURE]: false,
        [INPUTS.ADD_DATE]: false,
        [INPUTS.BORDES]: true,
        [INPUTS.NEW_FORMAT]: true,
        [INPUTS.CERTIFICATION_AT_ORIGIN]: false,
        [INPUTS.INCLUDE_BUDGET]: false,
        [INPUTS.INCLUDE_UNCERTIFIED_LINE_ITEMS]: false,
        [INPUTS.CERTIFICATION_PDF_FORMAT]: "detailed",
      },
      [DOWNLOAD_TYPES.EXCEL]: {
        [INPUTS.CLIENT_PRICE]: true,
        [INPUTS.COST_PRICE]: false,
        [INPUTS.PART_DECOMPOSITION]: true,
        [INPUTS.MEASURES]: false,
        [INPUTS.COMMENTS]: false,
        [INPUTS.DECOMPOSITION_FILTER]: 0,
        [INPUTS.CERTIFICATION_AT_ORIGIN]: false,
      },
      [DOWNLOAD_TYPES.BC3]: {
        [INPUTS.USE_COSTS]: true,
        [INPUTS.EXPORT_MEASUREMENTS]: true,
        [INPUTS.EXPORT_DECOMPOSITIONS]: true,
        [INPUTS.SIMPLE_MODE]: true,
      },
    },
  )

  const updateConfig = (section, key, value) => {
    setDownloadBudgetConfig(prevConfig => ({
      ...prevConfig,
      [section]: {
        ...prevConfig[section],
        [key]: value,
      },
    }))
  }

  return [downloadBudgetConfig, updateConfig]
}

export default useDownloadBudgetConfig
